import React from "react";
import { MapPin, ArrowRight } from "lucide-react";
import type { BackendOrder } from "./types";
import { DEFAULT_PRODUCT_IMAGE } from "../../utils/imageUtils";
import { useNavigate } from "react-router-dom";

interface OrderCardProps {
  order: BackendOrder;
}

const OrderCard: React.FC<OrderCardProps> = ({ order }) => {
  const getStatusColor = (stageName: string) => {
    const s = stageName.toLowerCase();
    if (["pending", "confirmed", "processing", "on hold"].includes(s))
      return "bg-accent-subtle text-accent-dark border border-accent/30";
    if (s === "shipped")
      return "bg-primary/5 text-primary border border-primary/20";
    if (s === "delivered" || s === "completed")
      return "bg-section-surface text-section-text border border-section-border";
    if (["cancelled", "returned", "refunded"].includes(s))
      return "bg-section-surface text-section-muted border border-section-border";
    return "bg-section-surface text-section-muted border border-section-border";
  };

  const formatCurrency = (amount: number | undefined | null) => {
    return `$${(amount ?? 0).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const navigate = useNavigate();

  const displayStatus = order.stage_name || "Unknown";

  return (
    <div className="p-6 hover:bg-section-surface transition-colors group border-l-4 border-l-transparent hover:border-l-primary">
      {/* Order Header */}
      <div className="flex flex-col sm:flex-row items-start justify-between mb-6 gap-4 sm:gap-0">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold text-section-text group-hover:text-primary transition-colors">
              Order #{order.id}
            </h3>
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                order.stage_name || "",
              )}`}
            >
              {displayStatus}
            </span>
          </div>
          <p className="text-sm text-section-muted flex items-center gap-2">
            <span className="text-section-text font-medium">
              {formatDate(order.created_at)}
            </span>
            <span className="w-1 h-1 bg-section-muted rounded-full"></span>
            <span>
              {order.products.length}{" "}
              {order.products.length === 1 ? "item" : "items"}
            </span>
          </p>
        </div>
        <div className="text-left sm:text-right">
          <p className="text-xs text-section-muted mb-1 uppercase tracking-wide font-semibold">
            Total Amount
          </p>
          <p className="text-xl sm:text-2xl font-bold text-section-text group-hover:text-primary transition-colors">
            {formatCurrency(order.total_usd_price)}
          </p>
        </div>
      </div>

      {/* Address */}
      {order.address_value && (
        <div className="flex items-start gap-3 text-sm text-section-text mb-6 p-4 bg-section-surface rounded-xl border border-section-border hover:border-section-card-hover-border transition-colors">
          <div className="w-8 h-8 rounded-full bg-section-bg flex items-center justify-center shrink-0 border border-section-border">
            <MapPin className="w-4 h-4 text-primary" />
          </div>
          <div className="mt-1">
            <span className="font-semibold text-section-text block mb-0.5">
              Shipping Address
            </span>
            <span className="text-section-muted">
              {order.address_value.street}, {order.address_value.city}
              {order.address_value.building &&
                `, ${order.address_value.building}`}
            </span>
          </div>
        </div>
      )}

      {/* Product Items */}
      <div className="space-y-4">
        {order.products.map((item) => {
          const imageUrl = item.image_paths?.[0]
            ? `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/product-images/${item.image_paths[0]}`
            : DEFAULT_PRODUCT_IMAGE;
          const variantOptions = item.variant_snapshot?.options;
          const hasDiscount = item.discount_rate > 0;

          return (
            <div
              key={item.order_product_id}
              onClick={() => navigate(`/product/${item.product_id}`)}
              className={`flex items-center gap-4 p-4 border rounded-xl transition-all cursor-pointer group/item ${
                item.is_cancelled
                  ? "bg-section-surface border-section-border opacity-60"
                  : "bg-section-surface border-section-border hover:bg-section-bg hover:border-section-card-hover-border"
              }`}
            >
              {/* Image */}
              <div className="relative shrink-0">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-section-surface border border-section-border">
                  <img
                    src={imageUrl}
                    alt={item.product_name || "Product"}
                    className="w-full h-full object-cover group-hover/item:scale-105 transition-transform duration-300"
                    onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_PRODUCT_IMAGE; }}
                  />
                </div>
                {hasDiscount && !item.is_cancelled && (
                  <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] px-2 py-0.5 rounded-full font-bold shadow-lg">
                    -{item.discount_rate}%
                  </span>
                )}
                {item.is_cancelled && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-lg">
                    Cancelled
                  </span>
                )}
              </div>

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <h5 className="font-bold text-section-text mb-1 line-clamp-1 group-hover/item:text-primary transition-colors text-base">
                  {item.product_name || "Unknown Product"}
                </h5>

                {variantOptions && Object.keys(variantOptions).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {Object.entries(variantOptions).map(([type, value]) => (
                      <span key={type} className="text-xs bg-section-bg border border-section-border text-section-text px-1.5 py-0.5 rounded-full">
                        <span className="text-section-muted">{type}:</span> {value}
                      </span>
                    ))}
                  </div>
                )}

                <p className="text-xs text-section-muted font-medium bg-section-bg inline-block px-2 py-1 rounded border border-section-border mb-2">
                  Qty: {item.quantity}
                </p>

                <div className="flex items-center justify-between">
                  <div>
                    {hasDiscount && (
                      <p className="text-xs text-section-muted line-through">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                    )}
                    <p className="text-lg font-bold text-section-text">
                      {formatCurrency(item.discounted_price * item.quantity)}
                    </p>
                  </div>
                  {!item.is_cancelled && (
                    <div className="w-8 h-8 rounded-full bg-section-surface border border-section-border flex items-center justify-center opacity-0 group-hover/item:opacity-100 transform translate-x-2 group-hover/item:translate-x-0 transition-all duration-300">
                      <ArrowRight className="w-4 h-4 text-primary" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderCard;
